import { useEffect, useState, useCallback } from "react";
import api from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";
import { useProtectedRoute } from "../../../hooks/useProtectedRoute";
import { Box, Button, MenuItem, Select, TextField, Typography, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody } from "@mui/material";

const categoryTypes = ["jobType", "deviceType", "brand", "model"];

export default function ManageCategoriesPage() {
    useProtectedRoute();
    const { token, role } = useAuth();
    const [selectedType, setSelectedType] = useState("jobType");
    const [categories, setCategories] = useState<any[]>([]);
    const [newName, setNewName] = useState("");

    const loadCategories = useCallback(async (type: string) => {
        try {
            const res = await api.get(`/categories/${type}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCategories(res.data);
        } catch {
            alert("Failed to load categories");
        }
    }, [token]);

    useEffect(() => {
        if (selectedType && token) {
            loadCategories(selectedType);
        }
    }, [selectedType, token, loadCategories]);

    const handleAdd = async () => {
        if (!newName || !selectedType) return;
        try {
            await api.post("/categories", 
                { type: selectedType, name: newName },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setNewName("");
            loadCategories(selectedType);  // reload list
        } catch {
            alert("Failed to add category");
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm("Are you sure you want to delete this?")) return;
        try {
            await api.delete(`/categories/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCategories(categories.filter(c => c._id !== id));
        } catch {
            alert("Only Admin can delete categories");
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" fontWeight={900} color="primary" gutterBottom>
                Manage Categories
            </Typography>

            {/* Select Type */}
            <Select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value as string)}
                size="small"
                sx={{ mb: 2, minWidth: 220 }}
            >
                {categoryTypes.map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
            </Select>

            {/* Add New */}
            <Box sx={{ display: "flex", gap: 1, mb: 2, maxWidth: 720 }}>
                <TextField
                    placeholder={`New ${selectedType} name`}
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    fullWidth
                />
                <Button onClick={handleAdd} variant="contained">Add</Button>
            </Box>

            {/* Categories List */}
            <TableContainer component={Paper} elevation={2} sx={{ maxWidth: 1200 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ backgroundColor: "rgba(255,255,255,0.04)" }}>
                            <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                            {role === "admin" && (
                                <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {categories.map((cat) => (
                            <TableRow key={cat._id} hover>
                                <TableCell>{cat.name}</TableCell>
                                {role === "admin" && (
                                    <TableCell align="right">
                                        <Button size="small" color="error" variant="contained" onClick={() => handleDelete(cat._id)}>
                                            Delete
                                        </Button>
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                        {categories.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={role === "admin" ? 2 : 1} align="center" sx={{ color: "text.secondary" }}>
                                    No categories yet.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
